import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import {
  MessagingChannelReplyMode,
  MessagingChannelType,
  postV1MessagingChannelsIdReply,
  postV1MessagingChannelsMessagingChannelIdMessages,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { useCallback } from 'react';

import { upsertMessageIntoCache } from '~hooks/useMessaging/messagingTranscriptCache';
import { MessagingChannel, MessagingMessage } from '~hooks/useMessaging/models';
import useMessagingChannelListBump from '~hooks/useMessaging/useMessagingChannelListBump';
import {
  MESSAGING_QUERY_KEY,
  messagingTranscriptQueryKey,
} from '~queries/messagingQueryKeys';
import type { MessagingMessagesPage } from '~queries/useMessagingMessagesInfiniteQuery';
import connection from '~services/connectionModule';

type SendMessageInput = {
  channel: MessagingChannel;
  clientMessageId: string;
  content: string;
  fileSignedIds: string[];
  repliedToBroadcastChannelId?: string;
};

type SentMessage = {
  channelId: string;
  /** Absent when the api rejected the send as one that already landed. */
  message?: MessagingMessage;
};

/**
 * The api holds `client_message_id` under a unique index per channel and sender,
 * so a 422 naming that field means this exact send already landed.
 */
const isReplayOfSentMessage = (error: unknown) => {
  if (!isAxiosError(error) || error.response?.status !== 422) return false;

  const errors: unknown = error.response.data?.error?.errors;
  return (
    Array.isArray(errors) &&
    errors.some((subError) => subError?.field === 'client_message_id')
  );
};

const sendFailureMessage = (error: unknown) =>
  isAxiosError(error) && error.response?.status === 429
    ? t('error_messages.messaging.too_many_messages')
    : t('error_messages.messaging.failed_to_post_message');

/**
 * A `reply_to_sender` send is routed through `/reply` and may land in another channel.
 * `clientMessageId` is the caller's: the api holds it under a unique index, so a
 * retry must reuse it and a rejected replay means the first attempt landed.
 */
export default function useSendMessage() {
  const queryClient = useQueryClient();
  const { bumpChannelInLists } = useMessagingChannelListBump();

  const sendMessage = useCallback(
    async ({
      channel,
      clientMessageId,
      content,
      fileSignedIds,
      repliedToBroadcastChannelId,
    }: SendMessageInput): Promise<SentMessage> => {
      let targetChannelId = channel.id;

      try {
        if (channel.replyMode === MessagingChannelReplyMode.REPLY_TO_SENDER) {
          const replyTarget = await postV1MessagingChannelsIdReply({
            path: { id: channel.id },
          });
          targetChannelId = replyTarget.data.data.id;
        }

        // An id the api will not accept fails the entire send, so it is attached only where
        // the origin is beyond doubt. The organizer of a `reply_to_sender` broadcast is
        // handed back that same broadcast, which is a post rather than a reply to it.
        const wasRoutedOutOfBroadcast =
          targetChannelId !== channel.id &&
          channel.channelType === MessagingChannelType.BROADCAST &&
          channel.replyMode === MessagingChannelReplyMode.REPLY_TO_SENDER;
        const explicitBroadcastChannelId =
          channel.channelType === MessagingChannelType.DIRECT
            ? repliedToBroadcastChannelId
            : undefined;
        const quotedBroadcastChannelId = wasRoutedOutOfBroadcast
          ? channel.id
          : explicitBroadcastChannelId;

        const response = await postV1MessagingChannelsMessagingChannelIdMessages({
          path: { 'messaging-channel-id': targetChannelId },
          body: {
            client_message_id: clientMessageId,
            // An attachment-only message carries no body, and the api rejects a send
            // that has neither.
            ...(content ? { content } : {}),
            ...(fileSignedIds.length ? { file_signed_ids: fileSignedIds } : {}),
            ...(quotedBroadcastChannelId
              ? { replied_to_broadcast_channel_id: quotedBroadcastChannelId }
              : {}),
          },
        });

        const message = MessagingMessage.parse(response.data.data);

        if (targetChannelId === channel.id) {
          const messagesQueryKey = messagingTranscriptQueryKey(channel.id);
          // Cancel an in-flight fetch so it cannot clobber the write below.
          await queryClient.cancelQueries({ queryKey: messagesQueryKey });

          const transcript =
            queryClient.getQueryData<InfiniteData<MessagingMessagesPage>>(
              messagesQueryKey,
            );

          if (transcript) {
            queryClient.setQueryData<InfiniteData<MessagingMessagesPage>>(
              messagesQueryKey,
              upsertMessageIntoCache(transcript, message),
            );
          } else {
            // The read this send cancelled has to run again: it is the only thing that can bring
            // back the history that was on its way.
            await queryClient.invalidateQueries({ queryKey: messagesQueryKey });
          }

          // Ahead of the socket echo, which is what brings the conversation in if unlisted.
          bumpChannelInLists(channel.id, message, { shouldReReadListsLacking: false });
        } else {
          await queryClient.invalidateQueries({ queryKey: [MESSAGING_QUERY_KEY] });
        }

        return { channelId: targetChannelId, message };
      } catch (error) {
        if (isReplayOfSentMessage(error)) {
          await queryClient.invalidateQueries({ queryKey: [MESSAGING_QUERY_KEY] });
          return { channelId: targetChannelId };
        }

        connection.handleRequestError(error, sendFailureMessage(error));
        throw error;
      }
    },
    [bumpChannelInLists, queryClient],
  );

  return { sendMessage };
}
